'use client';

import { useMutation, type UseMutationOptions } from '@tanstack/react-query';
import type { AppError, AsyncResult } from '@shared/utils';
import { Logger } from '@shared/logger';
import { useNotification } from './useNotification';
import type { NotificationVariant } from '../types/NotificationVariant';

interface UseResultMutationOptions<TData, E, TVariables>
  extends Omit<UseMutationOptions<TData, E, TVariables>, 'mutationFn'> {
  /** 
   * Whether to show notification on error.
   * @default true - set to false for silent operations
   */
  showNotification?: boolean;
  /** Custom title for error notification */
  notificationTitle?: string;
  /** 
   * Notification variant. 
   * @default 'error'
   */
  notificationVariant?: NotificationVariant;
}

/**
 * @group Hooks
 * @summary React Query mutation wrapper that integrates with the Result pattern.
 * Automatically handles error logging and notifications.
 * 
 * @example
 * ```tsx
 * function CreateUserForm() {
 *   const mutation = useResultMutation(
 *     (data: CreateUserInput) => tryCatchAsync(() => createUser(data)),
 *     {
 *       notificationTitle: 'Failed to Create User',
 *       onSuccess: () => {
 *         queryClient.invalidateQueries({ queryKey: ['users'] });
 *       }
 *     }
 *   );
 * 
 *   const handleSubmit = (data: CreateUserInput) => {
 *     mutation.mutate(data);
 *   };
 * 
 *   return (
 *     <form onSubmit={handleSubmit}>
 *       {mutation.isPending && <Spinner />}
 *       {mutation.isError && <ErrorMessage>{mutation.error.errorMessage}</ErrorMessage>}
 *       ...
 *     </form>
 *   );
 * }
 * ```
 * 
 * @example
 * ```tsx
 * // Silent mutation (no notification)
 * const mutation = useResultMutation(
 *   (id: string) => tryCatchAsync(() => deleteItem(id)),
 *   { showNotification: false }
 * );
 * ```
 */
export function useResultMutation<TData, TVariables, E = AppError>(
  mutationFn: (variables: TVariables) => AsyncResult<TData, E>,
  options?: UseResultMutationOptions<TData, E, TVariables>
) {
  const { notify } = useNotification();

  return useMutation({
    ...options,
    mutationFn: async (variables: TVariables) => {
      const result = await mutationFn(variables);

      return result.match({
        ok: (data) => data,
        err: (error) => {
          // Re-throw for React Query to handle
          throw error;
        }
      });
    },
    onError: (error, variables, context) => {
      // Always log errors
      Logger.error('Mutation failed', {
        resource: 'result.mutation',
        errorCode: (error as AppError).errorCode,
      }, new Error((error as AppError).errorMessage));

      // Show notification unless explicitly suppressed
      if (options?.showNotification !== false) {
        notify({
          title: options?.notificationTitle || 'Error',
          message: (error as AppError).errorMessage || 'An unexpected error occurred',
          variant: options?.notificationVariant || 'error',
        });
      }
      
      // Call user's onError if provided
      options?.onError?.(error, variables, context);
    },
  });
}

